import { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../lib/auth';
import { register } from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Alert, AlertDescription } from '../components/ui/alert';
import { Separator } from '../components/ui/separator';
import { Loader2, Ticket, AlertCircle, Search, Building2, User, CheckCircle } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function RegisterPage() {
    const [formData, setFormData] = useState({
        nome: '',
        cognome: '',
        email: '',
        password: '',
        confirmPassword: '',
        telefono: '',
        // Company data
        partita_iva: '',
        ragione_sociale: '',
        codice_fiscale: '',
        indirizzo: '',
        citta: '',
        cap: '',
        provincia: '',
        pec: ''
    });
    const [error, setError] = useState('');
    const [loading, setLoading] = useState(false);
    const [searchingCompany, setSearchingCompany] = useState(false);
    const [companyFound, setCompanyFound] = useState(false);
    const { loginUser } = useAuth();
    const navigate = useNavigate();

    const handleChange = (e) => {
        setFormData({ ...formData, [e.target.name]: e.target.value });
        if (e.target.name === 'partita_iva') {
            setCompanyFound(false);
        }
    };

    const searchCompany = async () => {
        if (!formData.partita_iva || formData.partita_iva.length < 11) {
            toast.error('Inserisci una Partita IVA valida (11 cifre)');
            return;
        }

        setSearchingCompany(true);
        try {
            const response = await axios.get(`${API_URL}/api/company/lookup/${formData.partita_iva}`);
            
            if (response.data.found && response.data.data) {
                const company = response.data.data;
                setFormData(prev => ({
                    ...prev,
                    ragione_sociale: company.ragione_sociale || prev.ragione_sociale,
                    codice_fiscale: company.codice_fiscale || prev.codice_fiscale,
                    indirizzo: company.indirizzo || prev.indirizzo,
                    citta: company.citta || prev.citta,
                    cap: company.cap || prev.cap,
                    provincia: company.provincia || prev.provincia,
                    pec: company.pec || prev.pec
                }));
                setCompanyFound(true);
                toast.success('Dati aziendali trovati!');
            } else {
                toast.error(response.data.message || 'Azienda non trovata');
            }
        } catch (error) {
            toast.error('Errore nella ricerca. Inserisci i dati manualmente.');
        } finally {
            setSearchingCompany(false);
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError('');

        if (formData.password !== formData.confirmPassword) {
            setError('Le password non coincidono');
            return;
        }

        if (formData.password.length < 6) {
            setError('La password deve essere di almeno 6 caratteri');
            return;
        }

        setLoading(true);

        try {
            const response = await register({
                nome: formData.nome,
                cognome: formData.cognome,
                email: formData.email,
                password: formData.password,
                telefono: formData.telefono,
                partita_iva: formData.partita_iva,
                ragione_sociale: formData.ragione_sociale,
                codice_fiscale: formData.codice_fiscale,
                indirizzo: formData.indirizzo,
                citta: formData.citta,
                cap: formData.cap,
                provincia: formData.provincia,
                pec: formData.pec
            });
            loginUser(response.data.token, response.data.user);
            navigate('/dashboard');
        } catch (err) {
            setError(err.response?.data?.detail || 'Errore durante la registrazione');
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white py-8 px-4">
            <div className="max-w-2xl mx-auto">
                {/* Header */}
                <div className="text-center mb-8">
                    <Link to="/" className="inline-flex items-center justify-center w-16 h-16 rounded-2xl bg-indigo-500 mb-6">
                        <Ticket className="w-8 h-8 text-white" />
                    </Link>
                    <h1 className="text-3xl font-bold text-slate-900">Crea un Account</h1>
                    <p className="mt-2 text-slate-600">Registrati per richiedere assistenza</p>
                </div>

                <Card className="border-slate-200 shadow-sm">
                    <CardHeader className="space-y-1 pb-4">
                        <CardTitle className="text-xl">Registrazione</CardTitle>
                        <CardDescription>Inserisci i tuoi dati personali e aziendali</CardDescription>
                    </CardHeader>
                    <CardContent>
                        <form onSubmit={handleSubmit} className="space-y-6">
                            {error && (
                                <Alert variant="destructive" className="bg-red-50 border-red-200">
                                    <AlertCircle className="h-4 w-4" />
                                    <AlertDescription>{error}</AlertDescription>
                                </Alert>
                            )}

                            {/* Personal Data Section */}
                            <div className="space-y-4">
                                <div className="flex items-center gap-2 text-sm font-medium text-slate-700">
                                    <User className="w-4 h-4" />
                                    Dati Personali
                                </div>
                                
                                <div className="grid grid-cols-2 gap-4">
                                    <div className="space-y-2">
                                        <Label htmlFor="nome">Nome *</Label>
                                        <Input
                                            id="nome"
                                            name="nome"
                                            placeholder="Mario"
                                            value={formData.nome}
                                            onChange={handleChange}
                                            required
                                            data-testid="register-nome-input"
                                        />
                                    </div>
                                    <div className="space-y-2">
                                        <Label htmlFor="cognome">Cognome *</Label>
                                        <Input
                                            id="cognome"
                                            name="cognome"
                                            placeholder="Rossi"
                                            value={formData.cognome}
                                            onChange={handleChange}
                                            required
                                            data-testid="register-cognome-input"
                                        />
                                    </div>
                                </div>

                                <div className="grid grid-cols-2 gap-4">
                                    <div className="space-y-2">
                                        <Label htmlFor="email">Email *</Label>
                                        <Input
                                            id="email"
                                            name="email"
                                            type="email"
                                            placeholder="nome@azienda.com"
                                            value={formData.email}
                                            onChange={handleChange}
                                            required
                                            data-testid="register-email-input"
                                        />
                                    </div>
                                    <div className="space-y-2">
                                        <Label htmlFor="telefono">Telefono</Label>
                                        <Input
                                            id="telefono"
                                            name="telefono"
                                            type="tel"
                                            placeholder="+39 333 1234567"
                                            value={formData.telefono}
                                            onChange={handleChange}
                                            data-testid="register-telefono-input"
                                        />
                                    </div>
                                </div>

                                <div className="grid grid-cols-2 gap-4">
                                    <div className="space-y-2">
                                        <Label htmlFor="password">Password *</Label>
                                        <Input
                                            id="password"
                                            name="password"
                                            type="password"
                                            placeholder="••••••••"
                                            value={formData.password}
                                            onChange={handleChange}
                                            required
                                            data-testid="register-password-input"
                                        />
                                    </div>
                                    <div className="space-y-2">
                                        <Label htmlFor="confirmPassword">Conferma Password *</Label>
                                        <Input
                                            id="confirmPassword"
                                            name="confirmPassword"
                                            type="password"
                                            placeholder="••••••••"
                                            value={formData.confirmPassword}
                                            onChange={handleChange}
                                            required
                                            data-testid="register-confirm-password-input"
                                        />
                                    </div>
                                </div>
                            </div>

                            <Separator />

                            {/* Company Data Section */}
                            <div className="space-y-4">
                                <div className="flex items-center gap-2 text-sm font-medium text-slate-700">
                                    <Building2 className="w-4 h-4" />
                                    Dati Aziendali
                                </div>

                                {/* VAT Lookup */}
                                <div className="space-y-2">
                                    <Label htmlFor="partita_iva">Partita IVA</Label>
                                    <div className="flex gap-2">
                                        <Input
                                            id="partita_iva"
                                            name="partita_iva"
                                            placeholder="12345678901"
                                            value={formData.partita_iva}
                                            onChange={handleChange}
                                            maxLength={11}
                                            data-testid="register-piva-input"
                                            className="flex-1"
                                        />
                                        <Button
                                            type="button"
                                            variant="outline"
                                            onClick={searchCompany}
                                            disabled={searchingCompany || formData.partita_iva.length < 11}
                                            data-testid="search-company-btn"
                                        >
                                            {searchingCompany ? (
                                                <Loader2 className="w-4 h-4 animate-spin" />
                                            ) : companyFound ? (
                                                <CheckCircle className="w-4 h-4 text-emerald-500" />
                                            ) : (
                                                <Search className="w-4 h-4" />
                                            )}
                                        </Button>
                                    </div>
                                    <p className="text-xs text-slate-500">
                                        Inserisci la P.IVA e clicca cerca per compilare automaticamente i dati
                                    </p>
                                </div>

                                <div className="space-y-2">
                                    <Label htmlFor="ragione_sociale">Ragione Sociale</Label>
                                    <Input
                                        id="ragione_sociale"
                                        name="ragione_sociale"
                                        placeholder="Nome Azienda S.r.l."
                                        value={formData.ragione_sociale}
                                        onChange={handleChange}
                                        data-testid="register-ragione-sociale-input"
                                    />
                                </div>

                                <div className="grid grid-cols-2 gap-4">
                                    <div className="space-y-2">
                                        <Label htmlFor="codice_fiscale">Codice Fiscale</Label>
                                        <Input
                                            id="codice_fiscale"
                                            name="codice_fiscale"
                                            placeholder="12345678901"
                                            value={formData.codice_fiscale}
                                            onChange={handleChange}
                                            data-testid="register-cf-input"
                                        />
                                    </div>
                                    <div className="space-y-2">
                                        <Label htmlFor="pec">PEC</Label>
                                        <Input
                                            id="pec"
                                            name="pec"
                                            type="email"
                                            placeholder="azienda@pec.it"
                                            value={formData.pec}
                                            onChange={handleChange}
                                            data-testid="register-pec-input"
                                        />
                                    </div>
                                </div>

                                <div className="space-y-2">
                                    <Label htmlFor="indirizzo">Indirizzo</Label>
                                    <Input
                                        id="indirizzo"
                                        name="indirizzo"
                                        placeholder="Via Roma, 1"
                                        value={formData.indirizzo}
                                        onChange={handleChange}
                                        data-testid="register-indirizzo-input"
                                    />
                                </div>

                                <div className="grid grid-cols-3 gap-4">
                                    <div className="space-y-2">
                                        <Label htmlFor="citta">Città</Label>
                                        <Input
                                            id="citta"
                                            name="citta"
                                            placeholder="Milano"
                                            value={formData.citta}
                                            onChange={handleChange}
                                            data-testid="register-citta-input"
                                        />
                                    </div>
                                    <div className="space-y-2">
                                        <Label htmlFor="cap">CAP</Label>
                                        <Input
                                            id="cap"
                                            name="cap"
                                            placeholder="20100"
                                            value={formData.cap}
                                            onChange={handleChange}
                                            maxLength={5}
                                            data-testid="register-cap-input"
                                        />
                                    </div>
                                    <div className="space-y-2">
                                        <Label htmlFor="provincia">Provincia</Label>
                                        <Input
                                            id="provincia"
                                            name="provincia"
                                            placeholder="MI"
                                            value={formData.provincia}
                                            onChange={handleChange}
                                            maxLength={2}
                                            data-testid="register-provincia-input"
                                        />
                                    </div>
                                </div>
                            </div>

                            <Button
                                type="submit"
                                className="w-full bg-indigo-500 hover:bg-indigo-600 h-12"
                                disabled={loading}
                                data-testid="register-submit-btn"
                            >
                                {loading ? (
                                    <>
                                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                                        Registrazione in corso...
                                    </>
                                ) : (
                                    'Registrati'
                                )}
                            </Button>
                        </form>

                        <div className="mt-6 text-center text-sm text-slate-600">
                            Hai già un account?{' '}
                            <Link to="/login" className="text-indigo-600 hover:text-indigo-500 font-medium">
                                Accedi
                            </Link>
                        </div>
                    </CardContent>
                </Card>
            </div>
        </div>
    );
}
